import React, { FC } from "react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { A11y, Pagination } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import Stack from "../stack/Stack";
import Typography from "../typography/Typography";
import { TrustPilotFiveStars } from "../svg/svgs/TrustPilotFiveStars";
import { TrustPilotFourStars } from "../svg/svgs/TrustPilotFourStars";
import "swiper/swiper-bundle.min.css";
import "swiper/css/pagination";
import { formatForID, useBackgroundColor } from "../../utils";
import TrustLogos from "../trustLogos/TrustLogos";
import ContainerGrid from "../containerGrid/ContainerGrid";
import { Review } from "../../types";
import { graphql, useStaticQuery } from "gatsby";

// Interface

export interface ReviewsProps {
  reviews: Review[];
  heading?: string;
  subheading: string;
}

interface ReviewIntro {
  heading: string;
  subheading: string;
}

// Styles

const StyledReviewIntroCard = styled.div`
  ${({ theme: { space, colors, media, borderRadii, borders } }) => css`
    ${useBackgroundColor("lilac")}
    padding: ${space.fluidMediumToLarge};
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: ${space.large};
    height: 100%;

    div {
      flex-shrink: 0;
    }
  `};
`;

const StyledReviewCard = styled.div`
  ${({ theme: { space, colors } }) => css`
    background-color: ${colors.white};
    padding: ${space.fluidSmallToMedium};
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: ${space.large};
    height: 100%;
  `};
`;

const StyledMobileReviews = styled.div`
  ${({ theme: { space, colors, maxMedia, borders, borderRadii } }) => css`
    display: none;
    > * {
      text-wrap: balance;
    }

    // Mobile only styles

    @media (max-width: ${maxMedia.medium}) {
      display: block;
      padding-bottom: 44px; // Magic no. for swiper pagination

      // Swiper styles

      .swiper {
        overflow: visible;
      }

      .swiper-slide {
        height: auto;
        max-width: 100%;
      }

      .swiper-button-next,
      .swiper-button-prev,
      .swiper-scrollbar {
        display: none;
      }

      .swiper-slide {
        height: auto;
        overflow: hidden;
        border-bottom: ${borders.darkBlue};
        border-top: ${borders.darkBlue};

        &:nth-of-type(1),
        &:nth-of-type(2),
        &:nth-of-type(3) {
          border-right: ${borders.darkBlue};
        }

        &:nth-of-type(1) {
          border-left: ${borders.darkBlue};
          border-top-left-radius: ${borderRadii.small};
          border-bottom-left-radius: ${borderRadii.small};
        }

        &:nth-of-type(4) {
          border-top-right-radius: ${borderRadii.small};
          border-bottom-right-radius: ${borderRadii.small};
          border-right: ${borders.darkBlue};
        }
      }

      .swiper-pagination {
        line-height: ${space.xxSmall};
        bottom: -44px;
        display: flex;
        justify-content: center;
        gap: ${space.xSmall};

        .swiper-pagination-bullet {
          margin: 0;
          height: ${space.xxSmall};
          width: ${space.medium};
          background-color: ${colors.white};
          border: ${borders.darkBlue};
          border-radius: ${borderRadii.medium};
          opacity: 1;
          transition: flex-grow 0.5s ease-in-out;
          flex-grow: 0.1;

          &.swiper-pagination-bullet-active {
            background-color: ${colors.darkBlue};
            flex-grow: 1;
          }
        }
      }
    }
  `};
`;

const StyledDesktopReviews = styled(ContainerGrid)`
  ${({ theme: { maxMedia } }) => css`
    > * {
      text-wrap: balance;
    }

    @media (max-width: ${maxMedia.medium}) {
      display: none;
    }
  `};
`;

const StyledLogo = styled.div`
  ${({ theme: { space, colors, borderRadii } }) => css`
    background-color: ${colors.white};
    padding: ${space.fluidXSmallToSmall};
    border: 1px solid currentColor;
    border-radius: ${borderRadii.small};
    display: inline-flex;
  `};
`;

// JSX

const ReviewIntroCard: FC<ReviewIntro> = ({ heading, subheading }) => (
  <StyledReviewIntroCard>
    <Stack gap="fluidSmallToMedium">
      <Typography component="h2" variant="headingMedium">
        {heading}
      </Typography>
      <Typography component="p" variant="secondaryHeadingSmall">
        {subheading}
      </Typography>
    </Stack>
    <div>
      <StyledLogo>
        <TrustLogos />
      </StyledLogo>
    </div>
  </StyledReviewIntroCard>
);

const ReviewCard: FC<Review> = ({ rating, heading, review, name }) => (
  <StyledReviewCard>
    <Stack gap="xSmall">
      {rating === "5" && <TrustPilotFiveStars />}
      {rating === "4" && <TrustPilotFourStars />}
      <Typography
        component="h3"
        variant="bodyLargeBold"
        title={`Reviewer heading: ${heading}`}
      >
        {heading}
      </Typography>
      <Typography component="p" variant="bodySmall" title={`Review: ${review}`}>
        {review}
      </Typography>
    </Stack>
    <Typography
      component="p"
      variant="bodySmallBold"
      title={`Reviewer name: ${name}`}
    >
      {name}
    </Typography>
  </StyledReviewCard>
);

export const Reviews: FC<ReviewsProps> = ({ reviews, heading, subHeading }) => {
  const data = useStaticQuery(graphql`
    query {
      settings: umbracoTrustpilotSettingsType {
        heading
        subHeading
      }
    }
  `);

  const { settings } = data;
  const reviewHeading = heading || settings?.heading;
  const reviewSubHeading = subHeading || settings?.subHeading;

  return (
    <div id={reviewHeading ? formatForID(reviewHeading) : ""}>
      <StyledMobileReviews>
        <Swiper
          modules={[Pagination, A11y]}
          spaceBetween={0}
          slidesPerView={1}
          navigation
          pagination={{ clickable: true }}
          scrollbar={{ draggable: true }}
        >
          <SwiperSlide>
            <ReviewIntroCard
              heading={reviewHeading}
              subheading={reviewSubHeading}
            />
          </SwiperSlide>
          {reviews?.map((review, index) => (
            <SwiperSlide key={index}>
              <ReviewCard {...review} />
            </SwiperSlide>
          ))}
        </Swiper>
      </StyledMobileReviews>
      <StyledDesktopReviews
        numberOfItems={reviews?.length + 1}
        display="featuredOnLeft"
      >
        <ReviewIntroCard
          heading={reviewHeading}
          subheading={reviewSubHeading}
        />
        {reviews?.map((review, index) => (
          <ReviewCard key={index} {...review} />
        ))}
      </StyledDesktopReviews>
    </div>
  );
};

export default Reviews;
